import { useState, useEffect } from 'react'
import { CreditCard, Plus, Pencil, Trash2, X } from 'lucide-react'
import { getAdminPlans, createPlan, updatePlan, deletePlan } from '../../lib/api'
import { formatCurrency } from '../../lib/utils'

const emptyPlan = {
  name: '', price_monthly_gbp: 0, price_yearly_gbp: 0,
  price_monthly_usd: 0, price_yearly_usd: 0,
  price_monthly_cad: 0, price_yearly_cad: 0,
  price_monthly_eur: 0, price_yearly_eur: 0,
  display_limit: 1, update_interval: 30,
  features: [], active: true,
  stripe_price_id_gbp: '', stripe_price_id_usd: '', stripe_price_id_cad: '', stripe_price_id_eur: '',
  stripe_price_id_gbp_yearly: '', stripe_price_id_usd_yearly: '', stripe_price_id_cad_yearly: '', stripe_price_id_eur_yearly: '',
}

export default function AdminPlans() {
  const [plans, setPlans] = useState([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(null)

  const load = () => getAdminPlans().then(data => { setPlans(data || []); setLoading(false) }).catch(() => setLoading(false))

  useEffect(() => { load() }, [])

  const handleSave = async (plan) => {
    if (plan.id) await updatePlan(plan.id, plan)
    else await createPlan(plan)
    setEditing(null)
    load()
  }

  const handleDelete = async (id) => {
    if (!confirm('Delete this plan? This cannot be undone.')) return
    await deletePlan(id)
    load()
  }

  if (loading) return <div className="p-8 flex items-center justify-center h-full"><div className="w-8 h-8 border-2 border-flight-cyan border-t-transparent rounded-full animate-spin" /></div>

  return (
    <div className="p-8">
      <div className="mb-8 flex items-center justify-between">
        <div><h1 className="text-2xl font-bold text-white">Plans</h1><p className="text-slate-400 text-sm">Manage pricing plans</p></div>
        <button onClick={() => setEditing({ ...emptyPlan })} className="px-4 py-2 rounded-lg bg-flight-cyan text-slate-950 font-bold text-sm flex items-center gap-2 hover:bg-flight-cyan/90">
          <Plus className="w-4 h-4" /> New Plan
        </button>
      </div>
      <div className="glass-panel overflow-hidden">
        <table className="w-full">
          <thead><tr className="border-b border-slate-700">
            <th className="text-left p-4 text-slate-400 text-xs font-medium">Plan</th>
            <th className="text-left p-4 text-slate-400 text-xs font-medium">Monthly</th>
            <th className="text-left p-4 text-slate-400 text-xs font-medium">Yearly</th>
            <th className="text-left p-4 text-slate-400 text-xs font-medium">Display Limit</th>
            <th className="text-left p-4 text-slate-400 text-xs font-medium">Status</th>
            <th className="text-right p-4 text-slate-400 text-xs font-medium">Actions</th>
          </tr></thead>
          <tbody>
            {plans.map(p => (
              <tr key={p.id} className="border-b border-slate-800/50 hover:bg-slate-800/20">
                <td className="p-4"><div className="flex items-center gap-3"><div className="w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center"><CreditCard className="w-4 h-4 text-slate-400" /></div><div><div className="text-white text-sm font-medium">{p.name}</div><div className="text-slate-500 text-xs font-mono">{p.id}</div></div></div></td>
                <td className="p-4 text-slate-300 text-sm font-mono">{formatCurrency(p.price_monthly_gbp)}</td>
                <td className="p-4 text-slate-300 text-sm font-mono">{formatCurrency(p.price_yearly_gbp)}</td>
                <td className="p-4 text-slate-300 text-sm">{p.display_limit}</td>
                <td className="p-4"><span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${p.active ? 'bg-emerald-500/10 text-emerald-400' : 'bg-slate-700 text-slate-400'}`}><span className={`w-1.5 h-1.5 rounded-full ${p.active ? 'bg-emerald-400' : 'bg-slate-500'}`} />{p.active ? 'Active' : 'Inactive'}</span></td>
                <td className="p-4">
                  <div className="flex items-center justify-end gap-2">
                    <button onClick={() => setEditing({ ...p, features: p.features || [] })} className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => handleDelete(p.id)} className="p-2 rounded-lg text-slate-400 hover:text-red-400 hover:bg-slate-800"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {plans.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-500 text-sm">No plans yet</td></tr>}
          </tbody>
        </table>
      </div>
      {editing && <PlanModal plan={editing} onSave={handleSave} onClose={() => setEditing(null)} />}
    </div>
  )
}

function PlanModal({ plan, onSave, onClose }) {
  const [form, setForm] = useState({ ...plan, features: (plan.features || []).join(', ') })
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value })
  const setNum = (key) => (e) => setForm({ ...form, [key]: Number(e.target.value) })

  const submit = (e) => {
    e.preventDefault()
    onSave({
      ...form,
      features: form.features.split(',').map(f => f.trim()).filter(Boolean),
    })
  }

  const priceField = (label, key) => (
    <div><label className="text-slate-400 text-xs mb-1 block">{label}</label><input type="number" min="0" value={form[key]} onChange={setNum(key)} className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm font-mono focus:border-flight-cyan focus:outline-none" /></div>
  )

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
      <div className="glass-panel w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-bold text-white">{form.id ? 'Edit Plan' : 'New Plan'}</h2>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div><label className="text-slate-400 text-sm mb-1 block">Name</label><input required type="text" value={form.name} onChange={set('name')} className="w-full px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white focus:border-flight-cyan focus:outline-none" /></div>

          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-slate-400 text-sm mb-1 block">Display Limit</label><input type="number" min="1" value={form.display_limit} onChange={setNum('display_limit')} className="w-full px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white focus:border-flight-cyan focus:outline-none" /></div>
            <div><label className="text-slate-400 text-sm mb-1 block">Update Interval (s)</label><input type="number" min="1" value={form.update_interval} onChange={setNum('update_interval')} className="w-full px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white focus:border-flight-cyan focus:outline-none" /></div>
          </div>

          <div>
            <div className="text-slate-400 text-sm mb-2">Pricing (minor units, e.g. 499 = £4.99)</div>
            <div className="grid grid-cols-4 gap-3 mb-3">
              {priceField('GBP / mo', 'price_monthly_gbp')}
              {priceField('USD / mo', 'price_monthly_usd')}
              {priceField('CAD / mo', 'price_monthly_cad')}
              {priceField('EUR / mo', 'price_monthly_eur')}
            </div>
            <div className="grid grid-cols-4 gap-3">
              {priceField('GBP / yr', 'price_yearly_gbp')}
              {priceField('USD / yr', 'price_yearly_usd')}
              {priceField('CAD / yr', 'price_yearly_cad')}
              {priceField('EUR / yr', 'price_yearly_eur')}
            </div>
          </div>

          <div>
            <div className="text-slate-400 text-sm mb-2">Stripe Price IDs (Monthly)</div>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <input type="text" placeholder="price_... (GBP)" value={form.stripe_price_id_gbp || ''} onChange={set('stripe_price_id_gbp')} className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm font-mono focus:border-flight-cyan focus:outline-none" />
              <input type="text" placeholder="price_... (USD)" value={form.stripe_price_id_usd || ''} onChange={set('stripe_price_id_usd')} className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm font-mono focus:border-flight-cyan focus:outline-none" />
              <input type="text" placeholder="price_... (CAD)" value={form.stripe_price_id_cad || ''} onChange={set('stripe_price_id_cad')} className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm font-mono focus:border-flight-cyan focus:outline-none" />
              <input type="text" placeholder="price_... (EUR)" value={form.stripe_price_id_eur || ''} onChange={set('stripe_price_id_eur')} className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm font-mono focus:border-flight-cyan focus:outline-none" />
            </div>
            <div className="text-slate-400 text-sm mb-2">Stripe Price IDs (Yearly)</div>
            <div className="grid grid-cols-2 gap-3">
              <input type="text" placeholder="price_... (GBP)" value={form.stripe_price_id_gbp_yearly || ''} onChange={set('stripe_price_id_gbp_yearly')} className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm font-mono focus:border-flight-cyan focus:outline-none" />
              <input type="text" placeholder="price_... (USD)" value={form.stripe_price_id_usd_yearly || ''} onChange={set('stripe_price_id_usd_yearly')} className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm font-mono focus:border-flight-cyan focus:outline-none" />
              <input type="text" placeholder="price_... (CAD)" value={form.stripe_price_id_cad_yearly || ''} onChange={set('stripe_price_id_cad_yearly')} className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm font-mono focus:border-flight-cyan focus:outline-none" />
              <input type="text" placeholder="price_... (EUR)" value={form.stripe_price_id_eur_yearly || ''} onChange={set('stripe_price_id_eur_yearly')} className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm font-mono focus:border-flight-cyan focus:outline-none" />
            </div>
          </div>

          <div><label className="text-slate-400 text-sm mb-1 block">Features (comma-separated)</label><textarea value={form.features} onChange={set('features')} rows={2} className="w-full px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm focus:border-flight-cyan focus:outline-none" /></div>

          <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={!!form.active} onChange={e => setForm({ ...form, active: e.target.checked })} className="rounded border-slate-700 bg-slate-800" /> Active</label>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-slate-400 hover:text-white text-sm">Cancel</button>
            <button type="submit" className="px-6 py-2 rounded-lg bg-flight-cyan text-slate-950 font-bold text-sm hover:bg-flight-cyan/90">Save</button>
          </div>
        </form>
      </div>
    </div>
  )
}
